import React, { useEffect, useRef, useState } from 'react';
import { ELEMENTS } from '../logic/elements';
import { RECIPES } from '../logic/recipes';
import './AlchemyTreeModal.css';

interface AlchemyTreeModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedElement?: string | null;
}

const levelNames: Record<number, string> = {
    1: "Basic",
    2: "Simple",
    3: "Complex",
    4: "Rare",
    5: "Legendary",
    6: "Philosopher's Stone"
};

export const AlchemyTreeModal: React.FC<AlchemyTreeModalProps> = ({
    isOpen,
    onClose,
    selectedElement
}) => {
    const [highlightedElement, setHighlightedElement] = useState<string | null>(null);
    const [hoveredElement, setHoveredElement] = useState<string | null>(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const [showInstructions, setShowInstructions] = useState<boolean>(true);
    const svgRef = useRef<SVGSVGElement>(null);
    const treeRef = useRef<HTMLDivElement>(null);
    const nodePositions = useRef<Record<string, { x: number; yTop: number; yBot: number }>>({});

    // Close modal on ESC
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // Highlight selected element on open
    useEffect(() => {
        if (isOpen && selectedElement) {
            setHighlightedElement(selectedElement);
        } else if (!isOpen) {
            setHighlightedElement(null);
        }
    }, [isOpen, selectedElement]);

    // Recalculate node positions and lines
    useEffect(() => {
        if (!isOpen) return;

        const calculatePositions = () => {
            const tree = treeRef.current;
            const svg = svgRef.current;
            if (!tree || !svg) return;

            const treeRect = tree.getBoundingClientRect();
            const scrollLeft = tree.parentElement?.scrollLeft || 0;

            // Collect positions of all nodes
            const positions: Record<string, { x: number; yTop: number; yBot: number }> = {};
            tree.querySelectorAll('.node').forEach((node) => {
                const el = node as HTMLElement;
                const id = el.dataset.id;
                if (!id) return;

                const rect = el.getBoundingClientRect();
                positions[id] = {
                    x: rect.left - treeRect.left + rect.width / 2 + scrollLeft,
                    yTop: rect.top - treeRect.top,
                    yBot: rect.bottom - treeRect.top
                };
            });

            nodePositions.current = positions;

            // Set SVG dimensions
            svg.style.width = tree.scrollWidth + 'px';
            svg.style.height = tree.scrollHeight + 'px';
            svg.setAttribute('viewBox', `0 0 ${tree.scrollWidth} ${tree.scrollHeight}`);
        };

        // Give DOM time to render
        const timeoutId = setTimeout(calculatePositions, 100);
        window.addEventListener('resize', calculatePositions);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', calculatePositions);
        };
    }, [isOpen]);

    // Functions for working with recipes
    const recipesFor = (id: string) => RECIPES.filter(r => r[1] === id);
    const leadsTo = (id: string) => [...new Set(RECIPES.filter(r => r[0].includes(id)).map(r => r[1]))];

    // Get related elements (for highlighting)
    const getRelatedElements = (id: string): Set<string> => {
        const related = new Set([id]);

        const addUp = (x: string) => {
            recipesFor(x).forEach(r => r[0].forEach(i => {
                if (!related.has(i)) {
                    related.add(i);
                    addUp(i);
                }
            }));
        };

        const addDown = (x: string) => {
            leadsTo(x).forEach(c => {
                if (!related.has(c)) {
                    related.add(c);
                    addDown(c);
                }
            });
        };

        addUp(id);
        addDown(id);

        return related;
    };

    const handleNodeClick = (id: string) => {
        if (highlightedElement === id) {
            setHighlightedElement(null);
        } else {
            setHighlightedElement(id);
        }
    };

    const handleNodeHover = (id: string, e: React.MouseEvent) => {
        setHoveredElement(id);
        setTooltipPos({ x: e.clientX, y: e.clientY });
    };

    const handleNodeLeave = () => {
        setHoveredElement(null);
    };

    if (!isOpen) return null;

    const relatedElements = highlightedElement ? getRelatedElements(highlightedElement) : new Set<string>();

    // Render connection lines
    const renderLines = () => {
        return RECIPES.map(([inputs, output], index) => {
            const outPos = nodePositions.current[output];
            if (!outPos) return null;

            return inputs.map((inp, i) => {
                const inPos = nodePositions.current[inp];
                if (!inPos) return null;

                const my = (inPos.yBot + outPos.yTop) / 2;
                const d = `M${inPos.x},${inPos.yBot} C${inPos.x},${my} ${outPos.x},${my} ${outPos.x},${outPos.yTop}`;

                const isActive = relatedElements.has(inp) && relatedElements.has(output);
                const isDim = highlightedElement && !isActive;

                return (
                    <path
                        key={`${index}-${i}`}
                        d={d}
                        className={`line ${isActive ? 'active' : ''} ${isDim ? 'dim' : ''}`}
                    />
                );
            });
        });
    };

    // Render tooltip
    const renderTooltip = () => {
        if (!hoveredElement) return null;

        const el = ELEMENTS[hoveredElement];
        const recs = recipesFor(hoveredElement);
        const leads = leadsTo(hoveredElement);

        return (
            <div
                className="tree-tooltip show"
                style={{
                    left: Math.min(tooltipPos.x + 10, window.innerWidth - 290),
                    top: Math.min(tooltipPos.y + 10, window.innerHeight - 250)
                }}
            >
                <div className="tt-title">{el.i} {el.n}</div>
                <div style={{ color: 'var(--dim)' }}>Level {el.l}</div>
                <div className="tt-section">
                    <div className="tt-label">Created from:</div>
                    {recs.length ? (
                        recs.map((r, i) => (
                            <div key={i} className="tt-item">
                                {ELEMENTS[r[0][0]].i} {ELEMENTS[r[0][0]].n} + {ELEMENTS[r[0][1]].i} {ELEMENTS[r[0][1]].n}
                            </div>
                        ))
                    ) : (
                        <div className="tt-item">Basic element</div>
                    )}
                </div>
                <div className="tt-section">
                    <div className="tt-label">Used for:</div>
                    {leads.length ? (
                        <div>
                            {leads.map(l => (
                                <span key={l} className="tt-tag">
                                    {ELEMENTS[l].i} {ELEMENTS[l].n}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <div className="tt-item">🏆 Victory!</div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="tree-modal-overlay" onClick={onClose}>
            <div className="tree-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="tree-close-btn" onClick={onClose}>✕</button>

                {showInstructions ? (
                    <div className="tree-instructions">
                        <button className="hide-instructions-btn" onClick={() => setShowInstructions(false)}>✕</button>
                        <div className="instruction-item highlight">
                            Click on element ✨ to see its recipe
                        </div>
                        <div className="instruction-item">
                            Press <kbd>R</kbd> to toggle tree
                        </div>
                        <div className="instruction-item">
                            Click <span className="ps-icon">🜛</span> Philosopher's Stone to see all paths
                        </div>
                    </div>
                ) : (
                    <button className="show-instructions-btn" onClick={() => setShowInstructions(true)}>?</button>
                )}

                <div className="tree-wrapper">
                    <div className="tree" ref={treeRef}>
                        <svg ref={svgRef} id="connections">
                            {renderLines()}
                        </svg>

                        {[6, 5, 4, 3, 2, 1].map((level) => {
                            const elements = Object.entries(ELEMENTS).filter(([_, el]) => el.l === level);
                            if (elements.length === 0) return null;

                            return (
                                <div key={level} className={`level l${level}`}>
                                    <div className="level-label">
                                        {levelNames[level]} ({elements.length})
                                    </div>
                                    <div className="nodes">
                                        {elements.map(([id, el]) => {
                                            const isActive = relatedElements.has(id);
                                            const isDim = highlightedElement && !isActive;

                                            return (
                                                <div
                                                    key={id}
                                                    className={`node ${isActive ? 'active' : ''} ${isDim ? 'dim' : ''}`}
                                                    data-id={id}
                                                    onClick={() => handleNodeClick(id)}
                                                    onMouseEnter={(e) => handleNodeHover(id, e)}
                                                    onMouseLeave={handleNodeLeave}
                                                >
                                                    <div className="node-icon">{el.i}</div>
                                                    <div className="node-name">{el.n}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {renderTooltip()}
            </div>
        </div>
    );
};